import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { createLogger } from "../utils/logger";

const logger = createLogger("WorkingHoursSelector");

const DAYS_OF_WEEK = [
  { key: "monday", label: "Monday", short: "Mon" },
  { key: "tuesday", label: "Tuesday", short: "Tue" },
  { key: "wednesday", label: "Wednesday", short: "Wed" },
  { key: "thursday", label: "Thursday", short: "Thu" },
  { key: "friday", label: "Friday", short: "Fri" },
  { key: "saturday", label: "Saturday", short: "Sat" },
  { key: "sunday", label: "Sunday", short: "Sun" },
];

const TIME_OPTIONS = [
  "00:00",
  "00:30",
  "01:00",
  "01:30",
  "02:00",
  "02:30",
  "03:00",
  "03:30",
  "04:00",
  "04:30",
  "05:00",
  "05:30",
  "06:00",
  "06:30",
  "07:00",
  "07:30",
  "08:00",
  "08:30",
  "09:00",
  "09:30",
  "10:00",
  "10:30",
  "11:00",
  "11:30",
  "12:00",
  "12:30",
  "13:00",
  "13:30",
  "14:00",
  "14:30",
  "15:00",
  "15:30",
  "16:00",
  "16:30",
  "17:00",
  "17:30",
  "18:00",
  "18:30",
  "19:00",
  "19:30",
  "20:00",
  "20:30",
  "21:00",
  "21:30",
  "22:00",
  "22:30",
  "23:00",
  "23:30",
];

interface WorkingHours {
  [key: string]: {
    start: string;
    end: string;
    enabled: boolean;
  };
}

interface WorkingHoursSelectorProps {
  value?: WorkingHours;
  onChange: (workingHours: WorkingHours) => void;
  bufferTime?: number;
  onBufferTimeChange?: (bufferTime: number) => void;
  showBufferTime?: boolean;
  error?: string;
  helperText?: string;
}

export const WorkingHoursSelector: React.FC<WorkingHoursSelectorProps> = ({ value = {}, onChange, bufferTime = 15, onBufferTimeChange, showBufferTime = true, error, helperText }) => {
  const [workingHours, setWorkingHours] = useState<WorkingHours>(() => {
    // Initialize with default working hours (9 AM - 5 PM Monday-Friday)
    const defaultHours: WorkingHours = {};
    DAYS_OF_WEEK.forEach((day) => {
      defaultHours[day.key] = {
        start: "09:00",
        end: "17:00",
        enabled: day.key !== "saturday" && day.key !== "sunday",
      };
    });
    return { ...defaultHours, ...value };
  });

  useEffect(() => {
    onChange(workingHours);
  }, [workingHours, onChange]);

  const handleDayToggle = (dayKey: string, enabled: boolean) => {
    setWorkingHours((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        enabled,
      },
    }));
  };

  const handleTimeChange = (dayKey: string, field: "start" | "end", time: string) => {
    setWorkingHours((prev) => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        [field]: time,
      },
    }));
  };

  const copyFromMonday = () => {
    const mondayHours = workingHours.monday;
    if (mondayHours) {
      const newHours = { ...workingHours };
      DAYS_OF_WEEK.forEach((day) => {
        if (day.key !== "monday") {
          const current = newHours[day.key] || { start: "09:00", end: "17:00", enabled: false };
          newHours[day.key] = {
            start: mondayHours.start,
            end: mondayHours.end,
            enabled: current.enabled, // preserve existing enabled state
          };
        }
      });
      setWorkingHours(newHours);
    }
  };

  const getEnabledDays = () => {
    return DAYS_OF_WEEK.filter((day) => workingHours[day.key]?.enabled);
  };

  const getWorkingDaysSummary = () => {
    const enabledDays = getEnabledDays();
    if (enabledDays.length === 0) return "No working days set";
    if (enabledDays.length === 7) return "Every day";
    if (enabledDays.length === 5 && enabledDays.every((day) => ["monday", "tuesday", "wednesday", "thursday", "friday"].indexOf(day.key) !== -1)) return "Monday - Friday";

    return enabledDays.map((day) => day.short).join(", ");
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Working Hours</Typography>
        <Button variant="outlined" size="small" onClick={copyFromMonday} disabled={!workingHours.monday?.enabled}>
          Copy from Monday
        </Button>
      </Box>

      {getEnabledDays().length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Working days: <strong>{getWorkingDaysSummary()}</strong>
            {getEnabledDays().length > 0 && (
              <span>
                {" "}
                • Hours:{" "}
                <strong>
                  {workingHours[getEnabledDays()[0].key]?.start} - {workingHours[getEnabledDays()[0].key]?.end}
                </strong>
              </span>
            )}
          </Typography>
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Day</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {DAYS_OF_WEEK.map((day) => (
              <TableRow key={day.key}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {day.label}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Checkbox checked={workingHours[day.key]?.enabled || false} onChange={(e) => handleDayToggle(day.key, e.target.checked)} />
                </TableCell>
                <TableCell>
                  {workingHours[day.key]?.enabled && (
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>Start</InputLabel>
                      <Select value={workingHours[day.key]?.start || "09:00"} onChange={(e) => handleTimeChange(day.key, "start", e.target.value)} label="Start">
                        {TIME_OPTIONS.map((time) => (
                          <MenuItem key={time} value={time}>
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </TableCell>
                <TableCell>
                  {workingHours[day.key]?.enabled && (
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel>End</InputLabel>
                      <Select value={workingHours[day.key]?.end || "17:00"} onChange={(e) => handleTimeChange(day.key, "end", e.target.value)} label="End">
                        {TIME_OPTIONS.map((time) => (
                          <MenuItem key={time} value={time}>
                            {time}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {showBufferTime && onBufferTimeChange && (
        <>
          <Divider sx={{ my: 3 }} />
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Buffer Time
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Additional time added before and after meetings to prevent conflicts.
            </Typography>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Buffer Time</InputLabel>
              <Select value={bufferTime} onChange={(e) => onBufferTimeChange(Number(e.target.value))} label="Buffer Time">
                <MenuItem value={0}>No buffer</MenuItem>
                <MenuItem value={5}>5 minutes</MenuItem>
                <MenuItem value={10}>10 minutes</MenuItem>
                <MenuItem value={15}>15 minutes</MenuItem>
                <MenuItem value={30}>30 minutes</MenuItem>
                <MenuItem value={45}>45 minutes</MenuItem>
                <MenuItem value={60}>1 hour</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </>
      )}

      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
          {helperText}
        </Typography>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};
